import $ from 'jquery';
import {computed, IReactionDisposer, observable, reaction, toJS} from 'mobx';
import InsightsTableStore from './InsightsTableStore';
import * as TokenBasedLoginAPI from '../apis/TokenBasedLoginAPI';
import * as SessionBasedLoginAPI from '../apis/SessionBasedLoginAPI';
import * as UserAPI from '../apis/UserAPI';
import MemoryBarStore, {HistoryHandler, HistoryItem} from './MemoryBarStore';
import DashboardStore from './DashboardStore';
import TimeIntervalStore, {DateRange, TimeState} from './TimeIntervalStore';
import {toast} from 'react-toastify';
import CompareStore, {CompareMode, CompareState} from './CompareStore';
import GeneralSelectorTableStore, {DataKey, SelectorTableColumnDef} from './GeneralSelectorTableStore';
import {MessageProvider} from '../i18n/i18n';
import VersionKeeper from '../info/VersionKeeper';
import {AsyncStorage} from '../storage/storage';
import ChannelStore, {ChannelState} from './ChannelStore';
import CurrencyStore, {CurrencyState} from './CurrencyStore';
import {TableSortDirection} from '../types/Table';
import OdtExportStore, {OdtExportState} from './OdtExportStore';
import {UrlKey, UrlState} from './UrlStore';
import ReportStore, {ReportState} from '../report/stores/ReportStore';
import ReportEditorStore, {ReportEditorState} from '../report/stores/ReportEditorStore';
import {DescriptiveKPIAdditions, isProductSlopeOutlierKPI, isSearchSlopeOutlierKPI, KPI} from '../kpi';
import {textFormat} from '../utility/FormatValueUtils';
import SearchSelectorTableStore from './searchterm/SearchSelectorTableStore';
import ProductSelectorTableStore from './product/ProductSelectorTableStore';
import CampaignSelectorTableStore from './campaign/CampaignSelectorTableStore';
import ProductLineDiagramStore from './product/ProductLineDiagramStore';
import SearchLineDiagramStore from './searchterm/SearchLineDiagramStore';
import CampaignLineDiagramStore from './campaign/CampaignLineDiagramStore';
import GeneralLineDiagramStore from './GeneralLineDiagramStore';
import {serializeDateRange} from '../utility/ApiParamConversion';
import {getSkipSlopeSetting} from '../storage/SettingsAPI';
import _ from 'lodash';

// @translateEnum(diagram.mode.)
export enum DiagramMode {
    None = 'none',
    Hourly = 'hourly',
    Daily = 'daily',
    TwentyFourHourMode = '24h',
}

export const tKeyDiagramMode = (mode: DiagramMode) => {
    return 'diagram.mode.' + mode;
};

export const tKeyAnalyticsTab = (analyticsTab: AnalyticsTab) => {
    return 'tab.' + analyticsTab;
};

const isAnalyticsTab = (value: any): value is AnalyticsTab => {
    return Object.values(AnalyticsTab).some((analyticsTab) => analyticsTab === value);
};

// @translateEnum(tab.)
export enum AnalyticsTab {
    Dashboard = 'Dashboard',
    SearchView = 'Suchansicht',
    ProductView = 'Produktansicht',
    ReportView = 'Reportansicht',
    CampaignView = 'Kampagnenansicht',
}

export const tKeyTab = (tab: AnalyticsTab) => {
    return 'tab.' + tab;
};

const defaultNumber = (n: number | undefined, defaultN: number) => (n === undefined ? defaultN : n);

export class Store implements HistoryHandler {
    // All stores are built in the constructor
    public insightsTableStore!: InsightsTableStore;
    public dashboardStore!: DashboardStore;
    public memoryBarStore!: MemoryBarStore;
    public timeIntervalStore!: TimeState;
    @observable public lineDiagramStore!: ProductLineDiagramStore | SearchLineDiagramStore | CampaignLineDiagramStore;
    @observable public selectorTableStore!: SearchSelectorTableStore | ProductSelectorTableStore | CampaignSelectorTableStore;
    public lineDiagramStoreSearch!: SearchLineDiagramStore;
    private selectorTableStoreSearch!: SearchSelectorTableStore;
    private lineDiagramStoreProduct!: ProductLineDiagramStore;
    private selectorTableStoreCampaign!: CampaignSelectorTableStore;
    private lineDiagramStoreCampaign!: CampaignLineDiagramStore;
    private selectorTableStoreProduct!: ProductSelectorTableStore;
    public currencyStore!: CurrencyState;
    public channelStore!: ChannelState;
    public compareStore!: CompareState;
    public odtExportStore!: OdtExportState;
    public reportStore!: ReportState;
    public reportEditorStore!: ReportEditorState;

    constructor(private t: MessageProvider, private storage: AsyncStorage, private versionKeeper: VersionKeeper, private urlStore: UrlState) {
        // register order is important
        this.setNewStores();
        this.setResizeListener();
        this.setupAnalyticsTabReaction();
    }

    // used for login procedure while being in development
    @observable public autologin = true;
    @observable public loggedIn = false;
    @observable public loginRequestRunning = false;
    @observable public diagramMode = DiagramMode.None;
    @observable public windowHeight = defaultNumber($(window).height(), 600);
    @observable public windowWidth = defaultNumber($(window).width(), 800);
    @observable public modalIsOpen = false;
    @observable public settingModalOpen = false;
    public analyticsTabReactionDisposer?: IReactionDisposer;
    public userName = '';
    public resizeTimeoutId = 0;
    public skipSlopes = false;

    public set activeAnalyticsTab(analyticsTab: AnalyticsTab) {
        this.urlStore.setAttributeToUrl(UrlKey.VIEW, analyticsTab);
        this.selectorTableStore = this.getSelectorTableStore();
    }

    @computed
    public get currentSearchTerm(): string {
        return this.urlStore.getAttributeFromHashParams(UrlKey.SEARCH_TERM, '');
    }

    @computed
    public get showCockpitLinks(): boolean {
        const showCockpitLink = this.urlStore.getAttributeFromHashParams(UrlKey.SHOW_COCKPIT_LINKS, 'false');

        return showCockpitLink.toLowerCase() === 'true';
    }

    public removeCurrentSearchTerm(): void {
        this.urlStore.setAttributeToUrl(UrlKey.SEARCH_TERM, '');
    }

    public navigateToCockpit(searchTerm: string): void {
        const channel = this.channelStore.selectedChannel;

        if (window.parent !== window && channel) {
            window.parent.postMessage({type: 'redirect', searchTerm, channel: channel.name}, '*');
        }
    }

    @computed
    public get activeAnalyticsTab(): AnalyticsTab {
        const analyticsTab = this.urlStore.getAttributeFromHashParams(UrlKey.VIEW, AnalyticsTab.Dashboard);

        if (!isAnalyticsTab(analyticsTab)) {
            return AnalyticsTab.Dashboard;
        }

        return analyticsTab;
    }

    @computed
    public get logoutButtonIsVisible(): boolean {
        return this.urlStore.getAttributeFromHashParams(UrlKey.FF_MODE, '0') === '0';
    }

    public selectedDateRangeChanged = (forceTriggerChannelChange: boolean) => {
        this.updateSearchProductDiagram();
        this.channelStore.loadChannelsForDateRange(this.timeIntervalStore.mainDateRange, forceTriggerChannelChange);
    };

    private setNewStores() {
        this.currencyStore = new CurrencyStore();
        const channelStore = new ChannelStore(
            this.selectedChannelChanged,
            this.availableChannelsChanged,
            this.storage,
            () => this.timeIntervalStore.mainDateRange,
            () => this.urlStore.getAttributeFromHashParams(UrlKey.CHANNEL, ''),
            (channelName: string) => this.urlStore.setAttributeToUrl(UrlKey.CHANNEL, channelName)
        );

        this.urlStore.listenToChannelChanges((channelName: string | null) => {
            if (!_.isEmpty(channelName) && (!this.channelStore.selectedChannel || this.channelStore.selectedChannel.name !== channelName)) {
                this.channelStore.setSelectedChannel(channelName!);
            }
        });

        const timeIntervalStore = new TimeIntervalStore(
            this.selectedDateRangeChanged,
            {
                interval: this.urlStore.getAttributeFromHashParams(UrlKey.INTERVAL, ''),
            },
            (dateRange: DateRange) => {
                const serializedDateRange = serializeDateRange(dateRange);
                if (serializedDateRange !== '') {
                    this.urlStore.setAttributeToUrl(UrlKey.INTERVAL, serializeDateRange(dateRange));
                }
            }
        );
        this.skipSlopes = false;
        this.channelStore = channelStore;
        this.timeIntervalStore = timeIntervalStore;
        this.lineDiagramStoreSearch = new SearchLineDiagramStore(this, this.channelStore, this.timeIntervalStore);
        this.selectorTableStoreSearch = new SearchSelectorTableStore(this, this.t, this.channelStore, this.timeIntervalStore);
        this.lineDiagramStoreProduct = new ProductLineDiagramStore(this, this.channelStore, this.timeIntervalStore);
        this.selectorTableStoreProduct = new ProductSelectorTableStore(this, this.t, this.channelStore, this.timeIntervalStore);

        this.lineDiagramStoreCampaign = new CampaignLineDiagramStore(this, this.channelStore, this.timeIntervalStore);
        this.selectorTableStoreCampaign = new CampaignSelectorTableStore(this, this.t, this.channelStore, this.timeIntervalStore);

        this.insightsTableStore = new InsightsTableStore(this);
        this.dashboardStore = new DashboardStore(this, this.t, this.channelStore, this.timeIntervalStore);
        this.lineDiagramStore = this.getLineDiagramStore();
        this.selectorTableStore = this.getSelectorTableStore();
        this.memoryBarStore = new MemoryBarStore(this, this.storage, this.channelStore, this.checkIfDataKeyExistsForAnalyticsTab);
        this.initReportStores();
        this.odtExportStore = new OdtExportStore(this.reportEditorStore.VISIBLE_DEFAULT_COLUMNS);

        const channelCompareStore = this.initChannelCompareStore();
        this.compareStore = channelCompareStore;

        this.memoryBarStore.register(this);
        this.memoryBarStore.register(channelStore);
        this.memoryBarStore.register(channelCompareStore);
        this.memoryBarStore.register(this.selectorTableStoreSearch);
        this.memoryBarStore.register(this.selectorTableStoreSearch.layout);
        this.memoryBarStore.register(this.selectorTableStoreProduct);
        this.memoryBarStore.register(this.selectorTableStoreProduct.layout);
        this.memoryBarStore.register(this.lineDiagramStoreSearch);
        this.memoryBarStore.register(this.lineDiagramStoreProduct);
        this.memoryBarStore.register(this.lineDiagramStoreCampaign);
        this.memoryBarStore.register(this.selectorTableStoreCampaign);
        this.memoryBarStore.register(timeIntervalStore);

        const currentSearchTerm = this.currentSearchTerm;
        if (currentSearchTerm) {
            this.removeCurrentSearchTerm();
            this.selectorTableStoreSearch.searchterm = currentSearchTerm;
            this.activeAnalyticsTab = AnalyticsTab.SearchView;
        }
    }

    public loadSkipSlopeSetting() {
        getSkipSlopeSetting()
            .then((value) => (this.skipSlopes = value.data))
            .catch((reason) => console.warn('error while getting skipSlope setting', reason));
    }

    private getTableColumnListWithChannelPerAnalyticsTab = (analyticsTab: AnalyticsTab): SelectorTableColumnDef[] => {
        const tableColumnList = this.getTableColumnListPerAnalyticsTab(analyticsTab);
        tableColumnList.splice(1, 0, {
            dataKey: DescriptiveKPIAdditions.Channel,
            label: this.t('table:channel', {count: 1}),
            width: 50,
            minWidth: 50,
            align: 'right',
            isSortable: false,
            valueFormat: textFormat(),
            channelComparisonEnabled: false,
        });
        return tableColumnList;
    };

    private initReportStores() {
        this.reportStore = new ReportStore({
            getMainDateRange: () => this.timeIntervalStore.mainDateRange,
            getTargetCurrency: () => this.currencyStore.targetCurrency ?? 'EUR',
            getTableColumnListWithChannelPerAnalyticsTab: this.getTableColumnListWithChannelPerAnalyticsTab,
        });
        this.reportEditorStore = new ReportEditorStore({
            setSidebarOpen: this.memoryBarStore.setSidebarOpen,
            messageProvider: this.t,
            getMainDateRange: () => this.timeIntervalStore.mainDateRange,
            getActiveAnalyticsTab: this.getActiveAnalyticsTab,
            setTargetCurrency: (targetCurrency: string) => (this.currencyStore.targetCurrency = targetCurrency),
            getTableColumnListPerAnalyticsTab: this.getTableColumnListPerAnalyticsTab,
            getHistoryItems: () => this.memoryBarStore.historyItems,
            getBookmarkItems: () => this.memoryBarStore.bookmarkItems,
            getAvailableReports: () => this.reportStore.availableReports,
            getTableColumnListWithChannelPerAnalyticsTab: this.getTableColumnListWithChannelPerAnalyticsTab,
            getTargetCurrency: () => this.currencyStore.targetCurrency,
            saveNewReport: this.reportStore.saveNewReport,
            saveExistingReport: this.reportStore.saveExistingReport,
        });
    }

    private availableChannelsChanged = () => {
        this.currencyStore.ensureTargetCurrency(this.channelStore.availableChannels);
        this.compareStore.ensureComparisionTargetCurrency(this.channelStore.availableChannels);
    };

    private updateSearchProductDiagram() {
        switch (this.activeAnalyticsTab) {
            case AnalyticsTab.SearchView:
                return this.lineDiagramStoreSearch.reloadDataIfNecessary();
            case AnalyticsTab.ProductView:
                return this.lineDiagramStoreProduct.reloadDataIfNecessary();
            case AnalyticsTab.CampaignView:
                return this.lineDiagramStoreCampaign.reloadDataIfNecessary();
            default:
                return;
        }
    }

    private selectedChannelChanged = (alsoUpdateSearchProductDiagram = false) => {
        switch (this.activeAnalyticsTab) {
            case AnalyticsTab.Dashboard:
                return this.dashboardStore.loadTableAndDiagramData();
            case AnalyticsTab.SearchView:
                return this.selectorTableStoreSearch.loadTableData();
            case AnalyticsTab.ProductView:
                if (alsoUpdateSearchProductDiagram) {
                    this.updateSearchProductDiagram();
                }
                return this.selectorTableStoreProduct.loadTableData();
            case AnalyticsTab.CampaignView:
                if (alsoUpdateSearchProductDiagram) {
                    this.updateSearchProductDiagram();
                }
                return this.selectorTableStoreCampaign.loadTableData();
            default:
                return;
        }
    };

    public setModalIsOpen(modalIsOpen: boolean) {
        this.modalIsOpen = modalIsOpen;
    }

    public getVersionInfo() {
        return this.versionKeeper.version;
    }

    public getPartialHistory = (): Partial<HistoryItem> => ({
        analyticsTab: this.activeAnalyticsTab,
        diagramMode: this.diagramMode,
    });

    public applyHistory = (history: HistoryItem): void => {
        this.activeAnalyticsTab = history.analyticsTab;
        this.diagramMode = history.diagramMode;
    };

    private setupAnalyticsTabReaction() {
        if (this.analyticsTabReactionDisposer !== undefined) {
            this.analyticsTabReactionDisposer();
        }

        this.analyticsTabReactionDisposer = reaction(
            () => this.activeAnalyticsTab,
            () => {
                this.lineDiagramStore = this.getLineDiagramStore();
                this.selectorTableStore = this.getSelectorTableStore();
            }
        );
    }

    public afterApplyHistory(history: HistoryItem) {
        this.selectedDateRangeChanged(true);
    }

    public getSearchtermAndProductSelectorTableStore(): {
        selectorTableStoreProduct: ProductSelectorTableStore;
        selectorTableStoreSearch: SearchSelectorTableStore;
    } {
        return {
            selectorTableStoreProduct: this.selectorTableStoreProduct,
            selectorTableStoreSearch: this.selectorTableStoreSearch,
        };
    }

    public setResizeListener() {
        $(window).resize(() => {
            clearTimeout(this.resizeTimeoutId);
            this.resizeTimeoutId = window.setTimeout(() => {
                this.windowHeight = defaultNumber($(window).height(), 600);
                this.windowWidth = defaultNumber($(window).width(), 800);
            }, 200);
        });
    }

    public getActiveAnalyticsTab = () => {
        return this.activeAnalyticsTab;
    };

    private getSelectorTableStore(): ProductSelectorTableStore | SearchSelectorTableStore | CampaignSelectorTableStore {
        if (this.activeAnalyticsTab === AnalyticsTab.SearchView) {
            return this.selectorTableStoreSearch;
        } else if (this.activeAnalyticsTab === AnalyticsTab.CampaignView) {
            return this.selectorTableStoreCampaign;
        }
        return this.selectorTableStoreProduct;
    }

    private getLineDiagramStore(): SearchLineDiagramStore | ProductLineDiagramStore | CampaignLineDiagramStore {
        if (this.activeAnalyticsTab === AnalyticsTab.SearchView) {
            return this.lineDiagramStoreSearch;
        } else if (this.activeAnalyticsTab === AnalyticsTab.CampaignView) {
            return this.lineDiagramStoreCampaign;
        }
        return this.lineDiagramStoreProduct;
    }

    /**
     * Because the main and compare date ranges are valid for all views now,
     * there needs to be a check, if the already loaded data for the view
     * still represents the selected date ranges. If they don't, the data needs to
     * be reloaded from the backend to fit the new date ranges.
     * @param tabName
     */
    public changeActiveAnalyticsTab = (tabName) => {
        this.activeAnalyticsTab = tabName;
        if (!this.timeIntervalStore.isMainDateRangeAvailable()) {
            return;
        }
        this.selectedChannelChanged(true);
    };

    public logoutUser = () => {
        window.localStorage.removeItem('visibleColumns');
        this.storage.clearCache();
        TokenBasedLoginAPI.logout();
        this.setNewStores();
        this.autologin = false;
        this.loggedIn = false;
        this.activeAnalyticsTab = AnalyticsTab.Dashboard;
    };

    public initialLogin() {
        if (this.loginRequestRunning) {
            return;
        }
        this.loginRequestRunning = true;
        this.loggedIn = false;
        this.autologin = false;

        SessionBasedLoginAPI.isSsoEnabled()
            .then((ssoEnabled) => {
                if (!ssoEnabled) {
                    this.checkTokens();
                } else {
                    SessionBasedLoginAPI.isLoggedInViaSSO()
                        .then((ssoLogin) => {
                            if (ssoLogin) {
                                this.onSuccessfulLogin();
                            } else {
                                this.checkTokens();
                            }
                        })
                        .catch(() => this.loginFailed());
                }
            })
            .catch(() => this.loginFailed());
    }

    private loginFailed() {
        toast.error(this.t('login.failed'));
        this.loginRequestRunning = false;
    }

    private onSuccessfulLogin() {
        this.loginRequestRunning = false;
        this.loggedIn = true;
        this.autologin = false;
        this.afterSuccessfulLogin();
    }

    private checkTokens() {
        TokenBasedLoginAPI.validateToken()
            .then((token) => {
                this.onSuccessfulLogin();
            })
            .catch(() => this.loginFailed());
    }

    public login(username: string, password: string) {
        if (this.loginRequestRunning) {
            return;
        }
        this.loginRequestRunning = true;
        this.loggedIn = false;
        this.autologin = false;

        TokenBasedLoginAPI.requestToken(username, password)
            .then((token) => {
                this.autologin = false;
                this.loginRequestRunning = false;
                this.loggedIn = true;
                this.afterSuccessfulLogin();
            })
            .catch(() => this.loginFailed());
    }

    private getTableLayoutForAnalyticsTab = (analyticsTab: AnalyticsTab): string => {
        switch (analyticsTab) {
            case AnalyticsTab.SearchView:
                return this.selectorTableStoreSearch.layout.getLayoutState();
            case AnalyticsTab.ProductView:
                return this.selectorTableStoreProduct.layout.getLayoutState();
            case AnalyticsTab.CampaignView:
                return this.selectorTableStoreCampaign.layout.getLayoutState();
            default:
                throw Error('SelectorTableLayout does not exist for analyticsTab ' + analyticsTab);
        }
    };

    public afterSuccessfulLogin() {
        UserAPI.getCurrentlyLoggedInUser()
            .then((username) => {
                this.userName = username;
                localStorage.setItem('username', username);
            })
            .catch(() => toast.error(this.t('loadingOfCurrentUsernameFailed')));
        this.timeIntervalStore.setDefaultMainDateRange().then(({mainDateRange}) => {
            this.memoryBarStore.createInitialHistoryItems(this.getTableLayoutForAnalyticsTab);
            this.channelStore.loadChannelsForDateRange(mainDateRange);
            this.currencyStore.loadExchangeRatesBasedOnEuro();
            this.memoryBarStore.loadHistoryItemsAndBookmarks();
            this.reportStore.loadReports();
            this.loadSkipSlopeSetting();
        });
    }

    public setDiagramMode(newDiagramMode: DiagramMode) {
        this.diagramMode = newDiagramMode;
    }

    private getTableColumnListPerAnalyticsTab = (analyticsTab: AnalyticsTab): SelectorTableColumnDef[] => {
        switch (analyticsTab) {
            case AnalyticsTab.ProductView:
                return this.selectorTableStoreProduct.tableColumnList;
            case AnalyticsTab.SearchView:
                return this.selectorTableStoreSearch.tableColumnList;
            default:
                throw Error(analyticsTab + ' does not have a tableColumnList');
        }
    };

    private checkIfDataKeyExistsForAnalyticsTab = (dataKey: KPI, analyticsTab: AnalyticsTab): boolean => {
        switch (analyticsTab) {
            case AnalyticsTab.SearchView:
                return this.selectorTableStoreSearch.tableHasDataKey(dataKey) || isSearchSlopeOutlierKPI(dataKey);
            case AnalyticsTab.ProductView:
                return this.selectorTableStoreProduct.tableHasDataKey(dataKey) || isProductSlopeOutlierKPI(dataKey);
            default:
                return false;
        }
    };

    private clearDiagramForStore(store: GeneralLineDiagramStore) {
        store.dataVersionChecker.clear();
        store.diagramData = [];
        store.termColorIndexes = [];
        store.selectedGraphInDiagram = [];
        store.requestedNormalDiagrams = [];
        store.requestedChannelDiagrams = [];
        store.loadedDiagramData = [];
    }

    private clearTable(store: GeneralSelectorTableStore) {
        store.dataVersionChecker.clear();
        store.tableData = {};
        store.activeTableFilters = [];
        store.useExactSearching = false;
        store.searchterm = '';
        store.loadedChannels = [];
        store.searchResult = [];
        store.channelSumRowInformation = {};
    }

    private clearDiagramAndTable = (): void => {
        [this.lineDiagramStoreSearch, this.lineDiagramStoreProduct].forEach((store) => this.clearDiagramForStore(store));
        [this.selectorTableStoreSearch, this.selectorTableStoreProduct].forEach((store) => this.clearTable(store));
    };

    private initChannelCompareStore() {
        return new CompareStore({
            updateMultigridLayout: () => this.selectorTableStore.recomputeMultiGridSizeIfPossible(),
            updateVisibleColumnsLayout: () => this.selectorTableStore.layout.updateVisibleColumns(),
            getTableColumnListForAnalyticsTab: this.getTableColumnListPerAnalyticsTab,
            getActiveAnalyticsTab: this.getActiveAnalyticsTab,
            getActiveTableFilters: () => this.selectorTableStore.activeTableFilters,
            getTableColumns: () => this.selectorTableStore.tableColumns(),
            selectedChannelChanged: this.selectedChannelChanged,
            setTableSort: (sortedColumn: DataKey, sortDirection: TableSortDirection | 'TOGGLE', compareMode?: CompareMode, compareChannel?: string) =>
                this.selectorTableStore.setTableSortWithoutReload(sortedColumn, sortDirection, compareMode, compareChannel),
            channelCompareIsAllowed: () => this.activeAnalyticsTab !== AnalyticsTab.CampaignView,
            clearDiagramAndTable: this.clearDiagramAndTable,
            setSelectedChannelWithoutReload: this.channelStore.setSelectedChannelWithoutReload,
            getSelectedChannel: () => this.channelStore.selectedChannel,
            getMainDateRange: () => this.timeIntervalStore.mainDateRange,
            setLastImportedDateForAbtest: this.channelStore.setLastImportedDateForAbTest,
            getAbTestParameterViaUrl: () => ({
                abTestId: this.urlStore.getAttributeFromHashParams(UrlKey.AB_TEST_ID, ''),
                variants: this.urlStore.getAttributeArrayFromHashParams(UrlKey.AB_VARIANT_ID, []),
            }),
            setAbTestParameterViaUrl: (abTestId: string, variants: string[]) => {
                this.urlStore.setAttributeToUrl(UrlKey.AB_TEST_ID, abTestId);
                this.urlStore.setAttributeToUrl(UrlKey.AB_VARIANT_ID, toJS(variants));
            },
        });
    }
}

export default Store;



// WEBPACK FOOTER //
// ./src/stores/store.ts